import { useState, useEffect, useCallback } from "react";
import SeamService from "../../../services/SeamService";

const SmartLocks = () => {
  const [settings, setSettings] = useState(null);
  const [portalUrl, setPortalUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [error, setError] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [checkInTime, setCheckInTime] = useState("15:00");
  const [checkOutTime, setCheckOutTime] = useState("11:00");
  const [timesSaving, setTimesSaving] = useState(false);
  const [timesSaved, setTimesSaved] = useState(false);

  // ── Load settings on mount ──────────────────────────────────────────
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await SeamService.getSettings();
      setSettings(data);
      setCheckInTime(data.standard_check_in_time || "15:00");
      setCheckOutTime(data.standard_check_out_time || "11:00");
    } catch (err) {
      console.error("Error loading Seam settings:", err);
      setError("Failed to load smart lock settings.");
    } finally {
      setLoading(false);
    }
  };

  // ── Toggle Seam on/off ──────────────────────────────────────────────
  const handleToggle = async () => {
    try {
      setError(null);
      const newEnabled = !settings.seam_enabled;
      const { data } = await SeamService.updateSettings({
        seam_enabled: newEnabled,
      });
      setSettings(data);

      // If enabling, auto-sync spaces
      if (newEnabled) {
        handleSyncSpaces();
      } else {
        setPortalUrl(null);
      }
    } catch (err) {
      console.error("Error toggling Seam:", err);
      setError("Failed to update smart lock settings.");
    }
  };

  // ── Toggle auto-generate codes ──────────────────────────────────────
  const handleAutoGenerateToggle = async () => {
    try {
      setError(null);
      const { data } = await SeamService.updateSettings({
        seam_auto_generate_codes: !settings.seam_auto_generate_codes,
      });
      setSettings(data);
    } catch (err) {
      console.error("Error updating auto-generate:", err);
      setError("Failed to update auto-generate setting.");
    }
  };

  // ── Load Customer Portal ────────────────────────────────────────────
  const loadPortal = useCallback(async () => {
    if (!settings?.seam_enabled) return;
    try {
      setPortalLoading(true);
      setError(null);
      const { data } = await SeamService.createPortal();
      setPortalUrl(data.url);
    } catch (err) {
      console.error("Error creating portal:", err);
      setError("Failed to load the smart lock management portal.");
    } finally {
      setPortalLoading(false);
    }
  }, [settings?.seam_enabled]);

  // Auto-load portal when Seam is enabled
  useEffect(() => {
    if (settings?.seam_enabled && !portalUrl) {
      loadPortal();
    }
  }, [settings?.seam_enabled, portalUrl, loadPortal]);

  // ── Sync spaces ─────────────────────────────────────────────────────
  const handleSyncSpaces = async () => {
    try {
      setSyncLoading(true);
      setSyncResult(null);
      setError(null);
      const { data } = await SeamService.syncSpaces();
      setSyncResult(
        `Successfully synced ${data.synced} ${data.synced === 1 ? "property" : "properties"} to Seam.`
      );
    } catch (err) {
      console.error("Error syncing spaces:", err);
      setError("Failed to sync properties. Please try again.");
    } finally {
      setSyncLoading(false);
    }
  };

  const handleSaveTimes = async () => {
    try {
      setTimesSaving(true);
      setTimesSaved(false);
      setError(null);
      const { data } = await SeamService.updateSettings({
        standard_check_in_time: checkInTime,
        standard_check_out_time: checkOutTime,
      });
      setSettings(data);
      setTimesSaved(true);
      setTimeout(() => setTimesSaved(false), 3000);
    } catch (err) {
      console.error("Error saving times:", err);
      setError("Failed to save check-in/out times.");
    } finally {
      setTimesSaving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <li>
        <div className="uk-text-center uk-padding">
          <div uk-spinner="ratio: 1"></div>
          <p className="uk-text-muted">Loading smart lock settings...</p>
        </div>
      </li>
    );
  }

  return (
    <li>
      <div className="uk-width-1-1">
        <h2 className="uk-heading-line">
          <span>Smart Locks</span>
        </h2>
        <p className="uk-text-muted uk-text-small">
          Connect your smart locks through Seam to automatically generate and
          manage door codes for guest reservations.
        </p>

        {/* Error Alert */}
        {error && (
          <div className="uk-alert uk-alert-danger" uk-alert="">
            <a
              className="uk-alert-close"
              uk-close=""
              onClick={() => setError(null)}
            ></a>
            <p>{error}</p>
          </div>
        )}

        {/* Success Alert */}
        {syncResult && (
          <div className="uk-alert uk-alert-success" uk-alert="">
            <a
              className="uk-alert-close"
              uk-close=""
              onClick={() => setSyncResult(null)}
            ></a>
            <p>{syncResult}</p>
          </div>
        )}

        {/* Enable/Disable Toggle */}
        <div className="uk-margin">
          <div className="uk-grid-small uk-flex-middle" uk-grid="">
            <div className="uk-width-auto">
              <label className="hms-switch">
                <input
                  type="checkbox"
                  checked={settings?.seam_enabled || false}
                  onChange={handleToggle}
                />
                <span className="hms-slider round"></span>
              </label>
            </div>
            <div className="uk-width-expand">
              <span className="uk-text-bold">
                {settings?.seam_enabled
                  ? "Smart Locks Enabled"
                  : "Enable Smart Locks"}
              </span>
            </div>
          </div>
        </div>

        {settings?.seam_enabled && (
          <>
            {/* Auto-generate codes toggle */}
            <div className="uk-margin">
              <div className="uk-grid-small uk-flex-middle" uk-grid="">
                <div className="uk-width-auto">
                  <label className="hms-switch">
                    <input
                      type="checkbox"
                      checked={settings?.seam_auto_generate_codes || false}
                      onChange={handleAutoGenerateToggle}
                    />
                    <span className="hms-slider round"></span>
                  </label>
                </div>
                <div className="uk-width-expand">
                  <span>Auto-generate door codes for new reservations</span>
                  <p className="uk-text-small uk-text-muted uk-margin-remove-top">
                    When enabled, Seam will automatically create time-bound
                    access codes for each reservation.
                  </p>
                </div>
              </div>
            </div>

            {/* Standard Check-in/Check-out Times */}
            <div className="uk-margin">
              <h4>Standard Check-in / Check-out Times</h4>
              <p className="uk-text-small uk-text-muted">
                Set your standard check-in and check-out times. These are used
                to schedule when access codes become active and expire.
              </p>
              <div className="uk-grid-small" uk-grid="">
                <div className="uk-width-1-4">
                  <label className="uk-form-label">Check-in Time</label>
                  <input
                    className="uk-input uk-form-small"
                    type="time"
                    value={checkInTime}
                    onChange={(e) => setCheckInTime(e.target.value)}
                  />
                </div>
                <div className="uk-width-1-4">
                  <label className="uk-form-label">Check-out Time</label>
                  <input
                    className="uk-input uk-form-small"
                    type="time"
                    value={checkOutTime}
                    onChange={(e) => setCheckOutTime(e.target.value)}
                  />
                </div>
                <div className="uk-width-expand uk-flex uk-flex-bottom">
                  <button
                    className="uk-button hms-btn hms-red-btn"
                    onClick={handleSaveTimes}
                    disabled={timesSaving}
                  >
                    {timesSaving ? (
                      <div data-uk-spinner="ratio: 0.5;"></div>
                    ) : timesSaved ? (
                      "Saved"
                    ) : (
                      "Save Times"
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Sync Properties Button */}
            <div className="uk-margin">
              <button
                className="uk-button uk-button-default uk-button-small"
                onClick={handleSyncSpaces}
                disabled={syncLoading}
              >
                {syncLoading ? (
                  <>
                    <span uk-spinner="ratio: 0.5"></span> Syncing...
                  </>
                ) : (
                  "Sync Properties to Seam"
                )}
              </button>
              <span className="uk-text-small uk-text-muted uk-margin-small-left">
                Push your property list to Seam so you can map locks to
                properties.
              </span>
            </div>

            <hr className="uk-divider-small" />

            {/* Customer Portal iFrame */}
            <div className="uk-margin">
              <h3>Lock Management Portal</h3>
              <p className="uk-text-small uk-text-muted">
                Use the portal below to connect your smart lock accounts and
                assign locks to your properties. Changes are saved automatically.
              </p>

              {portalLoading ? (
                <div className="uk-text-center uk-padding">
                  <div uk-spinner="ratio: 1"></div>
                  <p className="uk-text-muted">
                    Loading lock management portal...
                  </p>
                </div>
              ) : portalUrl ? (
                <div
                  style={{
                    border: "1px solid #e5e5e5",
                    borderRadius: "4px",
                    overflow: "hidden",
                  }}
                >
                  <iframe
                    src={portalUrl}
                    title="Seam Smart Lock Portal"
                    style={{
                      width: "100%",
                      height: "700px",
                      border: "none",
                    }}
                    allow="clipboard-write"
                    sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-top-navigation"
                  />
                </div>
              ) : (
                <div className="uk-text-center uk-padding">
                  <p className="uk-text-muted">
                    Could not load the portal.{" "}
                    <a onClick={loadPortal} style={{ cursor: "pointer" }}>
                      Try again
                    </a>
                  </p>
                </div>
              )}

              <button
                className="uk-button uk-button-text uk-button-small uk-margin-small-top"
                onClick={() => {
                  setPortalUrl(null);
                  loadPortal();
                }}
              >
                Refresh Portal
              </button>
            </div>
          </>
        )}
      </div>
    </li>
  );
};

export default SmartLocks;
